// Fuente de las filas de la cartera: UNA sola llamada paginada a `GET /backoffice/credits`, con el
// bloqueo del equipo ya resuelto en cada fila.
import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { loanCreditApi } from '@/api/services/loan-credit';
import { userMessageOf } from '@/api/errors';
import type { PortfolioRow } from '../types';
import { toPortfolioRow } from './mappers';

const PORTFOLIO_KEY = ['credit-portfolio'] as const;

/** Página del servidor. Mayor que `PORTFOLIO_PAGE_SIZE` para no pedir dos veces por página pintada. */
const SERVER_PAGE_SIZE = 50;

const STALE_TIME_MS = 30_000;

export type PortfolioSourceState =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | {
          status: 'ready';
          rows: readonly PortfolioRow[];
          /** `true` = lo cargado NO es toda la cartera; los conteos lo tienen que decir. */
          hasNextPage: boolean;
          fetchNextPage: () => void;
          isFetchingNextPage: boolean;
          /** Por qué falló la ÚLTIMA página pedida. `null` = no falló; las filas de antes siguen. */
          nextPageError: string | null;
      };

const LOADING: PortfolioSourceState = { status: 'loading' };

/**
 * La cartera del tenant, acumulando páginas. `enabled` en `false` no dispara nada: sin el permiso
 * de crédito el listado responde 403 y pedirlo solo gasta un viaje.
 */
export function usePortfolioSource(enabled: boolean): PortfolioSourceState {
    const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError } =
        useInfiniteQuery({
            queryKey: PORTFOLIO_KEY,
            queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
                loanCreditApi.listBackofficeCredits({
                    limit: SERVER_PAGE_SIZE,
                    ...(pageParam === undefined ? {} : { nextToken: pageParam }),
                }),
            initialPageParam: undefined as string | undefined,
            getNextPageParam: (last) => last.nextToken,
            enabled,
            staleTime: STALE_TIME_MS,
            retry: false,
            // Volver a la pestaña relanzaría TODAS las páginas acumuladas, una por una.
            refetchOnWindowFocus: false,
        });

    // useMemo OBLIGATORIO: sin él el flatMap devuelve un arreglo nuevo en cada render y la tabla
    // entra en re-render infinito, que congela la pantalla.
    const rows = useMemo(
        () => data?.pages.flatMap((page) => page.items.map(toPortfolioRow)) ?? [],
        [data],
    );

    return useMemo(() => {
        // El error solo vacía la pantalla cuando no hay NADA cargado. Con páginas en mano, tirarlas
        // por un tropiezo de red sería lo contrario de acumular.
        if (error && !data) return { status: 'error', message: userMessageOf(error) };
        if (!data) return LOADING;
        return {
            status: 'ready',
            rows,
            hasNextPage,
            fetchNextPage: () => {
                void fetchNextPage();
            },
            isFetchingNextPage,
            nextPageError: isFetchNextPageError ? userMessageOf(error) : null,
        };
    }, [data, error, rows, hasNextPage, fetchNextPage, isFetchingNextPage, isFetchNextPageError]);
}

/** ¿Hay filas que contar? Con `false`, las stat-pills muestran «—» y nunca un `0`. */
export function hasPortfolioRows(
    state: PortfolioSourceState,
): state is Extract<PortfolioSourceState, { status: 'ready' }> {
    return state.status === 'ready';
}
